import { useParams, Link, Navigate } from "react-router-dom";
import DevDocsShell from "@/components/DevDocsShell";
import { Copy, AlertTriangle, Wrench, ChevronLeft, ExternalLink, Zap, DollarSign, Clock } from "lucide-react";
import { toast } from "sonner";

const base = typeof window !== "undefined" ? window.location.origin : "";

/**
 * Rich integration recipes. Each slug maps to a hand-written walkthrough with
 * numbered steps, dark terminal code blocks, callouts, and a round-trip
 * example. Written to be shippable in ~15 minutes by a RevOps engineer.
 */
const RECIPES = {
  hubspot: {
    name: "HubSpot",
    tagline: "Enrich a contact the moment it enters HubSpot",
    endpoint: "POST /api/v1/leads/enrich?mode=sync",
    credits: "1 enrichment credit per contact (shared with your dashboard quota)",
    latency: "20–40 s inline (sync mode); use async + webhook if you expect bursts",
    warning:
      "HubSpot workflow custom-code actions have a 20 s execution limit on Pro and 60 s on Enterprise. If your enrichments routinely exceed that, switch to the async pattern at the bottom of this page.",
    steps: [
      {
        title: "Create a scoped API key",
        body: (
          <>
            In <b>LeadIQ Pro → Settings → API</b>, create a key labelled <code>HubSpot workflow</code>.
            Copy the <code>liq_live_…</code> secret <b>once</b> — it won't be shown again.
          </>
        ),
      },
      {
        title: "Create custom contact properties in HubSpot",
        body: (
          <>
            In <b>HubSpot → Settings → Properties → Contact properties</b>, create:
            <ul className="list-disc ml-5 mt-2 text-sm space-y-1">
              <li><code>leadiq_opportunity_score</code> (Number)</li>
              <li><code>leadiq_tags</code> (Multiline text)</li>
              <li><code>leadiq_summary</code> (Multiline text)</li>
              <li><code>leadiq_hiring_signals</code> (Multiline text)</li>
              <li><code>leadiq_opening_line</code> (Single line text)</li>
            </ul>
            Add more as you need them — each corresponds to a key from the{" "}
            <Link to="/developers/glossary" className="text-brand-700 hover:underline">
              enriched-field glossary
            </Link>
            .
          </>
        ),
      },
      {
        title: "Build a workflow",
        body: (
          <>
            <b>Automation → Workflows → Create workflow → Contact-based</b>. Enrollment trigger:
            <br />
            <i>"Contact property Email is known AND Contact property Company is known."</i> Add a
            <b> Custom code action</b> (requires Operations Hub Professional or above).
          </>
        ),
      },
      {
        title: "Paste the custom code",
        code: `// HubSpot Workflow Custom Code — Node 18
// Secrets: set LEADIQ_API_KEY as a workflow secret (don't inline it)
exports.main = async (event, callback) => {
  const email = event.inputFields['email'];
  const company = event.inputFields['company'];
  const domain = email && email.includes('@') ? email.split('@')[1] : null;
  if (!domain || !company) return callback({ outputFields: {} });

  const resp = await fetch("${base}/api/v1/leads/enrich?mode=sync", {
    method: "POST",
    headers: {
      "Authorization": "Bearer " + process.env.LEADIQ_API_KEY,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      lead: {
        email,
        name: event.inputFields['firstname'] + ' ' + event.inputFields['lastname'],
        title: event.inputFields['jobtitle'],
        company,
        domain,
      },
    }),
  });

  if (!resp.ok) {
    const retryAfter = resp.headers.get('retry-after');
    throw new Error('LeadIQ ' + resp.status + ' — retry-after=' + retryAfter);
  }
  const data = await resp.json();
  const e = data.enrichment || {};

  callback({
    outputFields: {
      leadiq_opportunity_score: e.opportunity_score,
      leadiq_tags: (e.tags || []).join(', '),
      leadiq_summary: e.company_overview?.summary || '',
      leadiq_hiring_signals: (e.intent_signals?.hiring_signals || []).join('\\n'),
      leadiq_opening_line: e.engagement_playbook?.opening_line || '',
    },
  });
};`,
      },
      {
        title: "Map output fields to properties",
        body: (
          <>
            Under <b>Output fields</b>, add one row per custom property (<code>leadiq_opportunity_score</code>,
            <code> leadiq_tags</code>, …). After the custom-code action, add a{" "}
            <b>Copy property value</b> step mapping each output to the matching contact property.
          </>
        ),
      },
      {
        title: "Test with one contact",
        body: (
          <>
            Toggle the workflow <b>on</b>, then enrol a single test contact. You should see the
            custom properties populate within ~40&nbsp;s. If you get a <code>429 upstream_full</code>,
            you've hit the platform-wide ceiling — retry in a few seconds (see the{" "}
            <Link to="/developers#limits" className="text-brand-700 hover:underline">
              rate-limit reference
            </Link>
            ).
          </>
        ),
      },
      {
        title: "(Optional) Async pattern for higher volume",
        body: (
          <>
            For higher volume or longer-tail accounts, call the async endpoint without
            <code> ?mode=sync</code>, pass a <code>callback_url</code> pointing at a HubSpot
            webhook, and update the contact in the webhook handler. See the{" "}
            <Link to="/developers#webhooks" className="text-brand-700 hover:underline">
              webhook payload shape
            </Link>
            {" "}on the overview page.
          </>
        ),
      },
    ],
  },
  pipedrive: {
    name: "Pipedrive",
    tagline: "Enrich a deal the moment it's created",
    endpoint: "POST /api/v1/leads/enrich?mode=sync",
    credits: "1 enrichment credit per deal",
    latency: "20–40 s inline (sync mode)",
    warning:
      "Pipedrive Automations don't natively retry on failure. If a call returns 429, the row won't auto-retry — add a filter to re-run on deals where leadiq_opportunity_score is empty.",
    steps: [
      {
        title: "Create an API key",
        body: (
          <>
            In <b>LeadIQ Pro → Settings → API</b>, create a key labelled <code>Pipedrive automation</code>.
          </>
        ),
      },
      {
        title: "Add custom deal fields",
        body: (
          <>
            In <b>Pipedrive → Settings → Data fields → Deal</b>, create <code>leadiq_opportunity_score</code>
            {" "}(Numerical), <code>leadiq_tags</code> (Text), and <code>leadiq_summary</code> (Large text).
          </>
        ),
      },
      {
        title: "Build an Automation",
        body: (
          <>
            <b>Automations → Create automation</b>. Trigger: <i>Deal → Created</i>. Add an action:
            <i> HTTP → Make a request</i>.
          </>
        ),
      },
      {
        title: "Configure the HTTP action",
        code: `URL
${base}/api/v1/leads/enrich?mode=sync

METHOD
POST

HEADERS
Authorization: Bearer liq_live_YOUR_KEY
Content-Type: application/json

BODY (JSON — use Pipedrive variables)
{
  "lead": {
    "name": "{{deal.person_name}}",
    "email": "{{deal.person_email}}",
    "title": "{{deal.person_title}}",
    "company": "{{deal.org_name}}",
    "domain": "{{deal.org_website}}"
  }
}`,
      },
      {
        title: "Add a follow-up action to write the score back",
        body: (
          <>
            Add a second action: <i>Update deal</i>. Map{" "}
            <code>leadiq_opportunity_score</code> to{" "}
            <code>&#123;&#123;previous.enrichment.opportunity_score&#125;&#125;</code>, and the summary field to
            <code> &#123;&#123;previous.enrichment.company_overview.summary&#125;&#125;</code>.
          </>
        ),
      },
      {
        title: "Test with one deal",
        body: "Enable the automation, create a test deal, watch the custom fields populate.",
      },
    ],
  },
  salesforce: {
    name: "Salesforce Flow",
    tagline: "Enrich a Lead or Contact via HTTP callout in a Flow",
    endpoint: "POST /api/v1/leads/enrich?mode=sync",
    credits: "1 enrichment credit per record",
    latency: "20–40 s per callout; keep inside a before-save or scheduled Flow",
    warning:
      "Salesforce HTTP callouts require a Named Credential — don't paste the API key as a plain string in the Flow. Use the Setup → Named Credentials → New flow and reference it as `callout:LeadIQ_Pro`.",
    steps: [
      {
        title: "Create a Named Credential",
        body: (
          <>
            In <b>Setup → Named Credentials → New Legacy</b>. Label <code>LeadIQ Pro</code>, Name <code>LeadIQ_Pro</code>,
            URL <code>{base}/api/v1</code>. Identity Type: <i>Named Principal</i>, Authentication Protocol:
            <i> Password Authentication</i>. Username blank, Password = your <code>liq_live_…</code> secret.
          </>
        ),
      },
      {
        title: "Create the HTTP callout action",
        body: (
          <>
            In <b>Setup → Object Manager → Lead → Flows → New Flow → Record-Triggered</b>.
            Trigger when a Lead is created or updated. Add an action of type{" "}
            <b>Action → HTTP Callout → New External Service</b>.
          </>
        ),
      },
      {
        title: "Define the external service schema",
        code: `# Paste this OpenAPI stub into the External Service wizard
openapi: 3.0.0
info:
  title: LeadIQ Pro
  version: "1.0"
servers:
  - url: callout:LeadIQ_Pro
paths:
  /leads/enrich:
    post:
      summary: Enrich a lead
      parameters:
        - name: mode
          in: query
          schema: { type: string, default: sync }
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
              properties:
                lead:
                  type: object
                  properties:
                    name:    { type: string }
                    email:   { type: string }
                    title:   { type: string }
                    company: { type: string }
                    domain:  { type: string }
      responses:
        "200":
          description: Enriched lead
          content:
            application/json:
              schema:
                type: object
                properties:
                  enrichment:
                    type: object`,
      },
      {
        title: "Call the service from the Flow",
        body: (
          <>
            In the Flow, after the record trigger, add an <b>Action → LeadIQ Pro / leads/enrich</b> step.
            Map <code>lead.email → $Record.Email</code>, <code>lead.company → $Record.Company</code>,
            <code>lead.domain → $Record.Website</code>, etc.
          </>
        ),
      },
      {
        title: "Write the response back to the record",
        body: (
          <>
            Add an <b>Update Records</b> step after the callout. Set <code>LeadIQ_Opportunity_Score__c</code>{" "}
            = <code>&#123;!Action_LeadIQ_Pro.enrichment.opportunity_score&#125;</code>. Repeat for other
            custom fields you want to persist.
          </>
        ),
      },
      {
        title: "Activate + test",
        body: "Save and Activate the Flow. Create a test Lead with Company + Email set. The custom fields should populate within ~40 s.",
      },
    ],
  },
  zapier: {
    name: "Zapier / Make / n8n",
    tagline: "Enrich on any trigger, from any no-code platform",
    endpoint: "POST /api/v1/leads/enrich?mode=sync",
    credits: "1 enrichment credit per Zap / scenario run",
    latency: "20–40 s per run (set your action's timeout to 60 s to be safe)",
    warning:
      "The generic HTTP step on Zapier free plans caps at 30 s. For sync enrichments, use Zapier's Premium Webhooks step (60 s cap) or switch to the async pattern.",
    steps: [
      {
        title: "Create an API key",
        body: (
          <>
            In <b>LeadIQ Pro → Settings → API</b>, create a key labelled{" "}
            <code>Zapier</code> (or <code>Make</code>, or <code>n8n</code>). Copy the secret.
          </>
        ),
      },
      {
        title: "Pick a trigger",
        body:
          "Zapier/Make/n8n all expose the same patterns: a trigger (new row, new email, new webhook, new Airtable record, etc.) that produces a lead you want enriched.",
      },
      {
        title: "Add the HTTP action",
        code: `URL
${base}/api/v1/leads/enrich?mode=sync

METHOD
POST

HEADERS
Authorization: Bearer liq_live_YOUR_KEY
Content-Type: application/json

BODY (JSON — map these from your trigger)
{
  "lead": {
    "name":    "{{Trigger.name}}",
    "email":   "{{Trigger.email}}",
    "title":   "{{Trigger.title}}",
    "company": "{{Trigger.company}}",
    "domain":  "{{Trigger.domain}}"
  }
}`,
      },
      {
        title: "Map the response",
        body: (
          <>
            The response has an <code>enrichment</code> object. In your next step (write to
            Airtable, send a Slack message, update your CRM, …), map the fields you care about:
            <code> enrichment.opportunity_score</code>,{" "}
            <code>enrichment.engagement_playbook.opening_line</code>,{" "}
            <code>enrichment.tags</code>, etc.
          </>
        ),
      },
      {
        title: "Handle 429s gracefully",
        body: (
          <>
            Add a <b>Delay / Pause</b> step before the HTTP action (2–5 s) if your workflow runs
            dozens of leads in a batch. Alternatively, catch <code>429</code> with a Zapier
            <i> Paths</i> step and retry after a 2&nbsp;s wait.
          </>
        ),
      },
      {
        title: "Test and turn on",
        body: "Zapier/Make/n8n all let you fire a single run against sample data. Verify the enrichment keys arrive, then enable the automation.",
      },
    ],
  },
};

export default function DevRecipe() {
  const { slug } = useParams();
  const recipe = RECIPES[slug];
  if (!recipe) return <Navigate to="/developers" replace />;

  const copy = (val) => navigator.clipboard.writeText(val).then(() => toast.success("Copied."));

  return (
    <DevDocsShell>
      <div data-testid={`recipe-${slug}-page`}>
        <Link
          to="/developers"
          className="text-ink-500 hover:text-ink-900 text-xs uppercase tracking-widestx font-mono inline-flex items-center gap-1 mb-6"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Developer docs
        </Link>

        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 flex items-center gap-1">
            <Wrench className="w-3 h-3" /> Integration recipe
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink-900 mt-2">
            {recipe.name}
          </h1>
          <p className="text-ink-500 text-base mt-2">{recipe.tagline}</p>
        </div>

        {/* Metadata row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <MetaCard label="Endpoint" value={recipe.endpoint} icon={<Zap className="w-3.5 h-3.5" />} mono />
          <MetaCard label="Cost" value={recipe.credits} icon={<DollarSign className="w-3.5 h-3.5" />} />
          <MetaCard label="Latency" value={recipe.latency} icon={<Clock className="w-3.5 h-3.5" />} />
        </div>

        {/* Warning callout */}
        {recipe.warning && (
          <div
            className="rounded-sm border border-amber-200 bg-amber-50 p-4 mb-8 flex items-start gap-3"
            data-testid="recipe-warning"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">{recipe.warning}</p>
          </div>
        )}

        {/* Steps */}
        <ol className="space-y-6">
          {recipe.steps.map((step, i) => (
            <li
              key={i}
              className="rounded-sm border border-ink-100 bg-ink-50 p-5"
              data-testid={`recipe-step-${i + 1}`}
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-mono text-xs uppercase tracking-widestx text-ink-400">
                  Step {i + 1}
                </span>
                <h3 className="font-display text-lg font-bold text-ink-900">{step.title}</h3>
              </div>
              {step.body && (
                <div className="text-sm text-ink-700 leading-relaxed">{step.body}</div>
              )}
              {step.code && (
                <div className="mt-3">
                  <div className="flex items-center justify-end mb-1">
                    <button
                      onClick={() => copy(step.code)}
                      className="inline-flex items-center gap-1 text-[11px] text-ink-500 hover:text-ink-900"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <pre className="bg-ink-900 text-ink-50 rounded-sm p-4 text-xs leading-relaxed overflow-x-auto font-mono">
                    {step.code}
                  </pre>
                </div>
              )}
            </li>
          ))}
        </ol>

        {/* Where next */}
        <div className="mt-10 rounded-sm border border-ink-100 bg-ink-50 p-5">
          <h2 className="font-display text-lg font-bold text-ink-900">Related</h2>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
            <Link to="/developers/reference" className="text-brand-700 hover:underline inline-flex items-center gap-1">
              Full API reference <ExternalLink className="w-3 h-3" />
            </Link>
            <Link to="/developers/glossary" className="text-brand-700 hover:underline inline-flex items-center gap-1">
              Enriched-field glossary <ExternalLink className="w-3 h-3" />
            </Link>
            {Object.keys(RECIPES)
              .filter((s) => s !== slug)
              .map((s) => (
                <Link
                  key={s}
                  to={`/developers/recipes/${s}`}
                  className="text-brand-700 hover:underline inline-flex items-center gap-1"
                >
                  {RECIPES[s].name} recipe <ExternalLink className="w-3 h-3" />
                </Link>
              ))}
          </div>
        </div>
      </div>
    </DevDocsShell>
  );
}

function MetaCard({ label, value, icon, mono }) {
  return (
    <div className="rounded-sm border border-ink-100 bg-white p-3">
      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`text-sm text-ink-900 mt-1 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
